import { Link } from "react-router-dom";
import { SrIcon } from "@/components/ui/SrIcon";
import { useFreelancers } from "@/hooks/use-freelancers";
import { useSetSupplierLink } from "@/hooks/use-freelancer-suppliers";
import { isFreelanceVoucher, supplierLinkFor } from "@/lib/freelancer-projects";
import type { DBFreelancerSupplier, DBVoucher } from "@/lib/db-types";
import { FreelancerPicker } from "./FreelancerPicker";

interface Props {
  voucher: DBVoucher;
  links: DBFreelancerSupplier[];
  canLink: boolean;
}

/**
 * VIB-2927: under leverandørnavnet i bilagstabellen. Viser frilanseren når
 * leverandøren er koblet, ellers «Koble til frilanser» på leverandørfakturaer.
 * Koblingen gjelder alle bilag fra leverandøren, også på andre prosjekter.
 */
export function SupplierFreelancerLink({ voucher, links, canLink }: Props) {
  const { data: freelancers = [] } = useFreelancers();
  const setLink = useSetSupplierLink();
  if (!isFreelanceVoucher(voucher)) return null;

  const link = supplierLinkFor(voucher, links);
  if (link?.is_not_freelancer) return null;
  if (link?.freelancer_id) {
    const name = freelancers.find((f) => f.id === link.freelancer_id)?.name ?? "Frilanser";
    return (
      <Link
        to={`/frilansere/${link.freelancer_id}`}
        className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-primary hover:underline"
      >
        <SrIcon name="film" className="h-3 w-3" />
        {name}
      </Link>
    );
  }
  if (!canLink) return null;

  const supplierName = voucher.supplier_name ?? `Leverandør ${voucher.po_supplier_id}`;
  return (
    <FreelancerPicker
      context={supplierName}
      onPick={(freelancerId) =>
        setLink.mutate({ po_supplier_id: voucher.po_supplier_id, supplier_name: supplierName, freelancer_id: freelancerId })
      }
      trigger={
        <button
          type="button"
          aria-label={`Koble ${supplierName} til frilanser`}
          className="mt-0.5 flex items-center gap-1 whitespace-nowrap text-[11px] text-muted-foreground hover:text-primary"
        >
          <SrIcon name="film" className="h-3 w-3" />
          Koble til frilanser
        </button>
      }
    />
  );
}
